/**
 * GPU procedural texture factory.
 * owner: materials
 *
 * Nothing is generated with CPU pixel loops. Each material family is a GLSL program
 * rendered fullscreen into render targets:
 *
 *   pass A  ->  MRT, RGBA16F x2   : linear albedo + coverage, and AO/rough/metal/height
 *   pass B  ->  MRT, RGBA8  x3    : sRGB albedo, ORM, tangent normal (+ height in .a)
 *
 * Pass A uses a scratch target that is shared between materials and thrown away at the
 * end of the boot, so the persistent cost of a material is exactly three 8-bit maps.
 * Height lives in the normal map's alpha channel, which is what POM samples.
 *
 * Everything is tileable: the noise library wraps the UV square onto a torus, so the
 * maps can be used with RepeatWrapping at any tiling rate without a seam.
 */

import * as THREE from 'three';
import { makeRng } from '../core/Rand.js';
import { NOISE_GLSL } from './shaders/noise.glsl.js';
import { SURFACE_COMMON, SURFACES } from './shaders/surfaces.glsl.js';
import {
  FS_VERT, SYNTH_FRAG_HEAD, SYNTH_FRAG_MAIN, RESOLVE_FRAG,
  BLUENOISE_SEED_FRAG, BLUENOISE_STEP_FRAG,
} from './shaders/passes.glsl.js';

/** Rebuilt bit-identically to the private table in Rand.js so GPU noise matches CPU noise. */
function buildPermutation() {
  const src = new Uint8Array(256);
  for (let i = 0; i < 256; i++) src[i] = i;
  const r = makeRng(20240613);
  for (let i = 255; i > 0; i--) {
    const j = Math.floor(r() * (i + 1));
    const t = src[i]; src[i] = src[j]; src[j] = t;
  }
  return src;
}

const MB = 1024 * 1024;

export class TextureFactory {
  /**
   * @param {THREE.WebGLRenderer} renderer
   * @param {{budgetMB?:number, quality?:string}} [opts]
   */
  constructor(renderer, opts = {}) {
    this.renderer = renderer;
    this.budgetBytes = (opts.budgetMB ?? 430) * MB;
    this.usedBytes = 0;
    this.quality = opts.quality ?? 'ultra';
    this.maxAnisotropy = renderer.capabilities.getMaxAnisotropy();

    this._cache = new Map();       // key -> { albedo, orm, normal, rt }
    this._programs = new Map();    // family -> RawShaderMaterial
    this._scratch = new Map();     // res -> WebGLRenderTarget (2 x RGBA16F)
    this._targets = [];            // every persistent RT, for dispose()

    /* permutation table */
    const perm = new THREE.DataTexture(buildPermutation(), 256, 1, THREE.RedFormat, THREE.UnsignedByteType);
    perm.minFilter = perm.magFilter = THREE.NearestFilter;
    perm.wrapS = perm.wrapT = THREE.RepeatWrapping;
    perm.generateMipmaps = false;
    perm.needsUpdate = true;
    this.perm = perm;

    /* fullscreen quad rig */
    this._quadScene = new THREE.Scene();
    this._quadCam = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
    this._quad = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), null);
    this._quad.frustumCulled = false;
    this._quadScene.add(this._quad);

    this._resolve = new THREE.RawShaderMaterial({
      glslVersion: THREE.GLSL3,
      vertexShader: FS_VERT,
      fragmentShader: RESOLVE_FRAG,
      depthTest: false, depthWrite: false,
      uniforms: {
        uAlbedo: { value: null },
        uOrmh: { value: null },
        uTexel: { value: new THREE.Vector2() },
        uNormalStrength: { value: 1 },
        uAoStrength: { value: 1 },
        uHeightScale: { value: 0.05 },
        uHeightContrast: { value: 4.0 },
        uPackRoughInNormalAlpha: { value: 0 },
      },
    });

    this.detailNormal = null;
    this.blueNoise = null;
  }

  /* ------------------------------------------------------------------ */

  _renderQuad(rt, material) {
    const r = this.renderer;
    const prevTarget = r.getRenderTarget();
    this._quad.material = material;
    r.setRenderTarget(rt);
    r.render(this._quadScene, this._quadCam);
    r.setRenderTarget(prevTarget);
  }

  _program(family) {
    let m = this._programs.get(family);
    if (m) return m;
    const body = SURFACES[family];
    if (!body) throw new Error(`TextureFactory: unknown surface family "${family}"`);
    m = new THREE.RawShaderMaterial({
      glslVersion: THREE.GLSL3,
      vertexShader: FS_VERT,
      fragmentShader: SYNTH_FRAG_HEAD + NOISE_GLSL + SURFACE_COMMON + body + SYNTH_FRAG_MAIN,
      depthTest: false, depthWrite: false,
      uniforms: {
        uPerm: { value: this.perm },
        uSeed: { value: 0 },
        uP: { value: Array.from({ length: 6 }, () => new THREE.Vector4()) },
        uC: { value: Array.from({ length: 4 }, () => new THREE.Color()) },
      },
    });
    this._programs.set(family, m);
    return m;
  }

  _scratchFor(res) {
    let rt = this._scratch.get(res);
    if (rt) return rt;
    rt = new THREE.WebGLRenderTarget(res, res, {
      count: 2,
      type: THREE.HalfFloatType,
      format: THREE.RGBAFormat,
      minFilter: THREE.LinearMipmapLinearFilter,   // top mip = field mean, used to
      magFilter: THREE.LinearFilter,                // renormalise height for POM
      wrapS: THREE.RepeatWrapping,
      wrapT: THREE.RepeatWrapping,
      depthBuffer: false,
      stencilBuffer: false,
      generateMipmaps: true,
    });
    this._scratch.set(res, rt);
    return rt;
  }

  /** Free the half-float scratch buffers. Call once the level is built. */
  releaseScratch() {
    for (const rt of this._scratch.values()) rt.dispose();
    this._scratch.clear();
  }

  /** Resolution the budget can afford for a material of the requested tier. */
  resolveRes(requested) {
    let res = requested;
    if (this.quality === 'low') res = Math.max(256, res >> 2);
    else if (this.quality === 'medium') res = Math.max(256, res >> 1);
    // three 8-bit maps + mip chain
    const cost = (r) => r * r * 4 * 3 * 1.34;
    while (res > 256 && this.usedBytes + cost(res) > this.budgetBytes) res >>= 1;
    return res;
  }

  /* ------------------------------------------------------------------ */

  /**
   * Generate (or fetch from cache) the map set for one material.
   * @param {string} key   cache key
   * @param {object} cfg   { family, res, seed, params:[[x,y,z,w]…], colors:[hex…],
   *                         normalStrength, aoStrength, heightScale, packRoughInAlpha }
   */
  generate(key, cfg) {
    const hit = this._cache.get(key);
    if (hit) return hit;

    const res = this.resolveRes(cfg.res ?? 1024);
    const scratch = this._scratchFor(res);

    /* ---- pass A : synthesize ---- */
    const prog = this._program(cfg.family);
    prog.uniforms.uSeed.value = cfg.seed ?? 0;
    const P = prog.uniforms.uP.value;
    for (let i = 0; i < 6; i++) {
      const v = (cfg.params && cfg.params[i]) || [0, 0, 0, 0];
      P[i].set(v[0] || 0, v[1] || 0, v[2] || 0, v[3] || 0);
    }
    const C = prog.uniforms.uC.value;
    for (let i = 0; i < 4; i++) {
      const c = (cfg.colors && cfg.colors[i]);
      if (c === undefined) C[i].setRGB(0.5, 0.5, 0.5, THREE.LinearSRGBColorSpace);
      else C[i].setHex(c, THREE.SRGBColorSpace); // -> linear working space
    }
    this._renderQuad(scratch, prog);

    /* ---- pass B : resolve to the sampled maps ---- */
    const out = new THREE.WebGLRenderTarget(res, res, {
      count: 3,
      type: THREE.UnsignedByteType,
      format: THREE.RGBAFormat,
      minFilter: THREE.LinearMipmapLinearFilter,
      magFilter: THREE.LinearFilter,
      wrapS: THREE.RepeatWrapping,
      wrapT: THREE.RepeatWrapping,
      anisotropy: this.maxAnisotropy,
      generateMipmaps: true,
      depthBuffer: false,
      stencilBuffer: false,
    });
    this._targets.push(out);

    // IMPORTANT: the colour space has to be set BEFORE the first render into the target.
    // three picks the GL internal format at allocation time — SRGB8_ALPHA8 for an sRGB
    // texture, RGBA8 otherwise — and that choice is what makes the hardware encode on
    // write and decode on sample. Setting it afterwards silently leaves the albedo
    // undecoded (washed out, over-bright) because there is no shader-side fallback.
    const [albedo, orm, normal] = out.textures;
    albedo.colorSpace = THREE.SRGBColorSpace;   // CONTRACTS.md §4
    orm.colorSpace = THREE.NoColorSpace;
    normal.colorSpace = THREE.NoColorSpace;

    const u = this._resolve.uniforms;
    u.uAlbedo.value = scratch.textures[0];
    u.uOrmh.value = scratch.textures[1];
    u.uTexel.value.set(1 / res, 1 / res);
    u.uNormalStrength.value = cfg.normalStrength ?? 1;
    u.uAoStrength.value = cfg.aoStrength ?? 1;
    u.uHeightScale.value = cfg.heightScale ?? 0.05;
    u.uHeightContrast.value = cfg.heightContrast ?? 4.0;
    u.uPackRoughInNormalAlpha.value = cfg.packRoughInAlpha ? 1 : 0;
    this._renderQuad(out, this._resolve);

    albedo.name = `${key}:albedo`;
    orm.name = `${key}:orm`;
    normal.name = `${key}:normal`;

    this.usedBytes += res * res * 4 * 3 * 1.34;

    const set = { albedo, orm, normal, res, rt: out };
    this._cache.set(key, set);
    return set;
  }

  /* ------------------------------------------------------------------ */

  /** Shared micro-surface layer: detail normal in RGB, micro-roughness in A. */
  getDetailNormal(res = 1024) {
    if (this.detailNormal) return this.detailNormal;
    const set = this.generate('__detail', {
      family: 'detail',
      res,
      seed: 91.7,
      normalStrength: 1.0,
      aoStrength: 0.0,
      heightScale: 0.010,
      packRoughInAlpha: true,
    });
    this.detailNormal = set.normal;
    this.detailNormal.name = 'detailNormal';
    return this.detailNormal;
  }

  /**
   * 512² blue noise, 4 decorrelated channels, RepeatWrapping + NearestFilter.
   * Exported for the other teams as `ctx.materials.blueNoise` (dithering, SSAO kernels,
   * volumetric jitter, decal scatter…).
   */
  getBlueNoise(size = 512) {
    if (this.blueNoise) return this.blueNoise;

    const mk = () => {
      const rt = new THREE.WebGLRenderTarget(size, size, {
        type: THREE.UnsignedByteType,
        format: THREE.RGBAFormat,
        minFilter: THREE.NearestFilter,
        magFilter: THREE.NearestFilter,
        wrapS: THREE.RepeatWrapping,
        wrapT: THREE.RepeatWrapping,
        depthBuffer: false, stencilBuffer: false, generateMipmaps: false,
      });
      return rt;
    };
    const a = mk(), b = mk();

    const seed = new THREE.RawShaderMaterial({
      glslVersion: THREE.GLSL3,
      vertexShader: FS_VERT, fragmentShader: BLUENOISE_SEED_FRAG,
      depthTest: false, depthWrite: false,
      uniforms: { uSize: { value: size } },
    });
    const step = new THREE.RawShaderMaterial({
      glslVersion: THREE.GLSL3,
      vertexShader: FS_VERT, fragmentShader: BLUENOISE_STEP_FRAG,
      depthTest: false, depthWrite: false,
      uniforms: {
        uSrc: { value: null },
        uTexel: { value: new THREE.Vector2(1 / size, 1 / size) },
        uSigma: { value: 1.9 },
      },
    });

    this._renderQuad(a, seed);
    let src = a, dst = b;
    const sigmas = [1.9, 1.5, 1.2, 1.0];
    for (let i = 0; i < sigmas.length; i++) {
      step.uniforms.uSrc.value = src.texture;
      step.uniforms.uSigma.value = sigmas[i];
      this._renderQuad(dst, step);
      const t = src; src = dst; dst = t;
    }
    seed.dispose();
    step.dispose();
    dst.dispose();

    this._targets.push(src);
    this.usedBytes += size * size * 4;
    this.blueNoise = src.texture;
    this.blueNoise.name = 'blueNoise512';
    this.blueNoise.colorSpace = THREE.NoColorSpace;
    return this.blueNoise;
  }

  stats() {
    return {
      materials: this._cache.size,
      programs: this._programs.size,
      textureMB: +(this.usedBytes / MB).toFixed(1),
      budgetMB: +(this.budgetBytes / MB).toFixed(1),
    };
  }

  dispose() {
    for (const rt of this._targets) rt.dispose();
    this.releaseScratch();
    for (const m of this._programs.values()) m.dispose();
    this._resolve.dispose();
    this._quad.geometry.dispose();
    this.perm.dispose();
    this._cache.clear();
    this._targets.length = 0;
  }
}
